// ─────────────────────────────────────────────────────────────────────────────
// WebSocket Event Types — shared across API, Dashboard, and Guest apps
// ─────────────────────────────────────────────────────────────────────────────

export const WS_EVENTS = {
  ORDER_CREATED: 'order:created',
  ORDER_UPDATED: 'order:updated',
  ORDER_STATUS_CHANGED: 'order:status-changed',
  ORDER_PAID: 'order:paid',
  ORDER_CANCELLED: 'order:cancelled',
  BILL_REQUESTED: 'order:bill-requested',
  // Item availability (86ing)
  ITEM_AVAILABILITY_CHANGED: 'item:availability-changed',
  ITEMS_AVAILABILITY_CHANGED: 'items:availability-changed',
  // Delivery integration
  DELIVERY_ORDER_RECEIVED: 'delivery:order-received',
  DELIVERY_STATUS_CHANGED: 'delivery:status-changed',
  // Inventory
  ITEM_STOCK_LOW: 'item:stock-low',
  // Table merge/unmerge
  TABLE_MERGED: 'table:merged',
  TABLE_UNMERGED: 'table:unmerged',
  // Single-table availability/status change (mark available/reserved, session open/close)
  TABLE_STATUS_CHANGED: 'table:status-changed',
  // KDS label refresh — fired on merge/unmerge so KDS updates table labels immediately
  KDS_TABLE_LABEL_CHANGED: 'kds:table-label-changed',
} as const;

export const WS_ROOMS = {
  venue: (restaurantId: string) => `venue:${restaurantId}`,
  /** Public, read-only room for anonymous guests. Carries ONLY item-availability
   *  (86) events — never order/table/bill data — so it's safe to expose without
   *  auth. Guests join tokenless via the `restaurantId` handshake query. */
  venueAvailability: (restaurantId: string) => `venue-availability:${restaurantId}`,
  order: (orderId: string) => `order:${orderId}`,
  group: (groupId: string) => `group:${groupId}`,
  /** Per-table room — guests subscribe here on page load (before any orders exist)
   *  so they receive merge/unmerge events even when they have no active orders. */
  table: (venueSlug: string, tableNumber: string) => `table:${venueSlug}:${tableNumber}`,
} as const;

// ── Event Payloads ────────────────────────────────────────────────────────────

export interface OrderCreatedPayload {
  orderId: string;
  orderNumber: number;
  orderType: string;
  status: string;
  kitchenStatus: string | null;
  barStatus: string | null;
  restaurantId: string;
}

export interface OrderStatusChangedPayload {
  orderId: string;
  restaurantId: string;
  status: string;
  kitchenStatus: string | null;
  barStatus: string | null;
  paymentStatus: string;
}

export interface OrderPaidPayload {
  orderIds: string[];
  restaurantId: string;
  paymentStatus: string;
}

export interface BillRequestedPayload {
  orderId: string;
  orderNumber: number;
  tableNumber: string | null;
  restaurantId: string;
  billRequestedAt: string | null;
}

export interface OrderCancelledPayload {
  orderId: string;
  orderNumber: number;
  restaurantId: string;
  cancellationReason: string | null;
}

// ── Table Group (merge) Payloads ──────────────────────────────────────────────

export interface TableMergedPayload {
  groupId: string;
  restaurantId: string;
  venueSlug: string;
  leadTableId: string;
  /** Member rows — each one's id is a restaurant_tables.id */
  members: Array<{ tableId: string; tableNumber: string; label: string | null }>;
  label: string | null;
  createdAt: string;
}

export interface TableUnmergedPayload {
  groupId: string;
  restaurantId: string;
  venueSlug: string;
  /** Full member info (tableId + tableNumber) for per-table room fan-out */
  releasedMembers: Array<{ tableId: string; tableNumber: string }>;
  /** Order → table reassignments that were applied during unmerge */
  reassignments: Record<string, string>;
  closedAt: string;
}

export interface KdsTableLabelChangedPayload {
  restaurantId: string;
  /** Table IDs whose display label changed (merged group label or back to individual) */
  affectedTableIds: string[];
}

export interface TableStatusChangedPayload {
  restaurantId: string;
  tableId: string;
  /** The new persisted restaurant_tables.status. */
  status: string;
}

